import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, CreditCard, FileText, Store, Loader2,
  ExternalLink, PartyPopper, ArrowRight, ShieldCheck, MessageCircle,
} from "lucide-react";

const BOXHERO_ACCESS_EMAIL = "3pl@boxhero.com";
const BOXHERO_LOGO_WHITE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";

const PLATFORMS = [
  { id: "shopify", label: "Shopify" },
  { id: "woocommerce", label: "WooCommerce" },
  { id: "etsy", label: "Etsy" },
  { id: "amazon", label: "Amazon" },
  { id: "other", label: "Other" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh, account } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding");
      setState(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // If a customer returns from the Stripe portal, re-check payment status.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("from") === "payment") {
      load();
      toast.info("Checking your payment method…");
    }
  }, [load]);

  const goDashboard = async () => {
    sessionStorage.setItem("bh_welcome_seen", "1");
    await refresh();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="onboarding-loading">
        <Loader2 className="w-6 h-6 animate-spin text-bh-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-10 bg-red-50 border border-red-200 rounded-xl p-6 text-red-700" data-testid="onboarding-error">
        {error}
      </div>
    );
  }

  const steps = state.steps || {};
  const required = state.required_steps || [];
  const doneCount = required.filter((s) => steps[s]?.done).length + 1; // +1 for dashboard access
  const totalCount = required.length + 1;

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="onboarding-page">
      <Header company={state.company_name} done={doneCount} total={totalCount} completed={state.completed} />

      {account?.whatsapp_group_url && <WhatsAppCard url={account.whatsapp_group_url} />}

      <div className="space-y-4 mt-6">
        <StepCard index={1} title="Access your dashboard" done icon={ShieldCheck}
          desc="You're signed in — welcome aboard!" testId="step-access" />

        {state.has_stripe_customer && (
          <PaymentStep done={steps.payment?.done} onDone={load} />
        )}

        <ContractStep contract={state.contract} done={steps.contract?.done} onDone={setState} />

        <StoreAccessStep step={steps.store_access} onDone={setState} />
      </div>

      {state.completed ? (
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="onboarding-complete">
          <PartyPopper className="w-8 h-8 text-emerald-600 mx-auto" />
          <p className="text-lg font-bold text-emerald-800 mt-2" style={{ fontFamily: "Manrope" }}>You're all set!</p>
          <p className="text-sm text-emerald-700 mt-1">Thanks for completing setup. Your dashboard is ready.</p>
          <button data-testid="onboarding-go-dashboard-done" onClick={goDashboard}
            className="mt-4 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
            Go to my dashboard <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-8 text-center">
          <button data-testid="onboarding-skip" onClick={goDashboard}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 underline underline-offset-2">
            I'll finish this later — take me to my dashboard
          </button>
        </div>
      )}
    </div>
  );
}

function WhatsAppCard({ url }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="onboarding-whatsapp"
      className="group flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 mt-4 hover:border-emerald-400 hover:shadow-sm transition-all">
      <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
        <MessageCircle className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-emerald-900" style={{ fontFamily: "Manrope" }}>Your support starts today on WhatsApp</p>
        <p className="text-sm text-emerald-800/80">Fast and easy — no tickets. Join your dedicated group and chat directly with our team.</p>
      </div>
      <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm whitespace-nowrap">
        Join group <ExternalLink className="w-4 h-4" />
      </span>
    </a>
  );
}

function Header({ company, done, total, completed }) {
  const pct = Math.round((done / total) * 100);
  return (
    <div className="bg-gradient-to-br from-bh-navy-deep to-[#34506a] rounded-2xl p-8 text-white" data-testid="onboarding-header">
      <img src={BOXHERO_LOGO_WHITE} alt="BoxHero" className="h-8 w-auto mb-5" data-testid="onboarding-logo" />
      <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
        {completed ? "Your account is ready" : `Let's get ${company || "you"} set up`}
      </h1>
      <p className="text-white/70 mt-2 text-sm max-w-lg">
        A few quick steps and you'll be ready to ship. This takes about 3 minutes.
      </p>
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-medium text-white/70 mb-1.5">
          <span>Setup progress</span>
          <span data-testid="onboarding-progress-label">{done} of {total} complete</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
          <div className="h-full bg-bh-orange rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function StepCard({ index, title, desc, done, icon: Icon, children, testId, accent = "blue" }) {
  const ring = done ? "border-emerald-200" : "border-slate-200";
  return (
    <div data-testid={testId} className={`bg-white border ${ring} rounded-xl p-5 transition-colors`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0">
          {done
            ? <CheckCircle2 className="w-7 h-7 text-emerald-500" data-testid={`${testId}-done-icon`} />
            : <Circle className="w-7 h-7 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${done ? "bg-emerald-50 text-emerald-600" : accent === "blue" ? "bg-bh-blue/10 text-bh-blue" : "bg-slate-100 text-slate-500"}`}>
              <Icon className="w-4 h-4" />
            </div>
            <h3 className="text-base font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>{title}</h3>
          </div>
          {desc && <p className="text-sm text-slate-500 mt-1.5 ml-9">{desc}</p>}
          {children && <div className="mt-4 ml-9">{children}</div>}
        </div>
      </div>
    </div>
  );
}

function PaymentStep({ done, onDone }) {
  const [busy, setBusy] = useState(false);
  const openPortal = async () => {
    setBusy(true);
    try {
      const returnUrl = `${window.location.origin}/welcome?from=payment`;
      const { data } = await api.post("/onboarding/payment-portal", { return_url: returnUrl });
      if (data.is_demo) {
        toast.error("Billing isn't fully configured yet — please contact BoxHero.");
        setBusy(false);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };
  return (
    <StepCard index={2} title="Add your payment method" done={done} icon={CreditCard} testId="step-payment"
      desc={done ? "Your card is on file — thank you!" : "Securely add a card so we can bill your fulfillment charges. We use Stripe — we never see your card details."}>
      {!done && (
        <div className="flex items-center gap-3">
          <button data-testid="payment-portal-btn" onClick={openPortal} disabled={busy}
            className="inline-flex items-center gap-2 bg-bh-blue hover:bg-bh-blue-dark disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Add payment method
          </button>
          <button data-testid="payment-recheck-btn" onClick={onDone}
            className="text-sm font-medium text-slate-500 hover:text-slate-700">I've added it — recheck</button>
        </div>
      )}
    </StepCard>
  );
}

function ContractStep({ contract, done, onDone }) {
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);

  const viewContract = async () => {
    if (!contract) return;
    setViewing(true);
    try {
      const { data } = await api.get(`/documents/files/${contract.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setViewing(false);
    }
  };

  const acknowledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/acknowledge-contract");
      onDone(data);
      toast.success("Agreement acknowledged");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard index={3} title="Review your service agreement" done={done} icon={FileText} testId="step-contract"
      desc={done ? "Thanks — you've reviewed your agreement." : undefined}>
      {!done && (
        !contract ? (
          <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="contract-pending">
            Your BoxHero team will upload your signed agreement shortly. Check back here once it's ready.
          </p>
        ) : (
          <div className="space-y-3">
            <button data-testid="contract-view-btn" onClick={viewContract} disabled={viewing}
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-bh-blue hover:text-bh-blue text-slate-700 font-semibold px-4 py-2.5 rounded-lg transition-colors">
              {viewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              View {contract.name || "agreement"}
            </button>
            <button data-testid="contract-ack-btn" onClick={acknowledge} disabled={busy}
              className="flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2.5 rounded-lg transition-colors w-full sm:w-auto justify-center">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              I've reviewed and agree
            </button>
          </div>
        )
      )}
    </StepCard>
  );
}

function StoreAccessStep({ step, onDone }) {
  const done = step?.done;
  const [platform, setPlatform] = useState("shopify");
  const [form, setForm] = useState({ myshopify_domain: "", collaborator_code: "", store_url: "", note: "" });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (platform === "shopify" && !form.myshopify_domain.trim()) {
      toast.error("Please enter your .myshopify.com domain."); return;
    }
    if (platform !== "shopify" && !form.store_url.trim()) {
      toast.error("Please enter your store URL."); return;
    }
    setBusy(true);
    try {
      const payload = { platform };
      if (platform === "shopify") {
        payload.myshopify_domain = form.myshopify_domain.trim();
        if (form.collaborator_code.trim()) payload.collaborator_code = form.collaborator_code.trim();
      } else {
        payload.store_url = form.store_url.trim();
      }
      if (form.note.trim()) payload.note = form.note.trim();
      const { data } = await api.post("/onboarding/store-access", payload);
      onDone(data);
      toast.success("Thanks — we'll request access shortly");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <StepCard index={4} title="Grant us access to your store" done={done} icon={Store} testId="step-store"
      desc={done ? `Access details received for ${labelFor(step?.platform)}. We'll take it from here.` : "So we can pull your orders and ship them, we need access to your e-commerce platform. Pick yours below."}>
      {!done && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="platform-grid">
            {PLATFORMS.map((p) => (
              <button key={p.id} type="button" data-testid={`platform-${p.id}`}
                onClick={() => setPlatform(p.id)}
                className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${platform === p.id ? "bg-bh-blue text-white border-bh-blue" : "bg-white text-slate-700 border-slate-200 hover:border-bh-blue"}`}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 leading-relaxed" data-testid="platform-instructions">
            <PlatformInstructions platform={platform} />
          </div>

          {platform === "shopify" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Your .myshopify.com domain" testId="store-myshopify-domain"
                placeholder="your-store.myshopify.com" value={form.myshopify_domain} onChange={onChange("myshopify_domain")} />
              <TextField label="Collaborator request code (4 digits)" testId="store-collaborator-code"
                placeholder="1234" value={form.collaborator_code} onChange={onChange("collaborator_code")} />
            </div>
          ) : (
            <TextField label="Your store URL" testId="store-url"
              placeholder="https://yourstore.com" value={form.store_url} onChange={onChange("store_url")} />
          )}

          <TextField label="Anything we should know? (optional)" testId="store-note"
            placeholder="e.g. I've added your email as a staff account" value={form.note} onChange={onChange("note")} />

          <button data-testid="store-access-submit" onClick={submit} disabled={busy}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            I've done this
          </button>
        </div>
      )}
    </StepCard>
  );
}

function labelFor(id) {
  return PLATFORMS.find((p) => p.id === id)?.label || id || "your store";
}

function PlatformInstructions({ platform }) {
  if (platform === "shopify") {
    return (
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Give us your permanent <strong>.myshopify.com</strong> URL (not your public domain) — find it in Shopify admin under <em>Settings</em>.</li>
        <li>In Shopify admin, go to <strong>Settings → Users → Security</strong> and copy your 4-digit <strong>collaborator request code</strong> (you can generate a new one there).</li>
        <li>Paste both below. We'll send a collaborator request — you'll approve it under <strong>Settings → Users → Requests</strong>.</li>
      </ol>
    );
  }
  if (platform === "woocommerce") {
    return (
      <ol className="list-decimal list-inside space-y-1.5">
        <li>In WordPress admin, go to <strong>Users → Add New</strong>.</li>
        <li>Create a user for <strong>{BOXHERO_ACCESS_EMAIL}</strong> with the role <strong>Shop Manager</strong> (or Administrator).</li>
        <li>Paste your store URL below and note anything we should know.</li>
      </ol>
    );
  }
  if (platform === "amazon") {
    return (
      <ol className="list-decimal list-inside space-y-1.5">
        <li>In Seller Central, go to <strong>Settings → User Permissions</strong>.</li>
        <li>Invite <strong>{BOXHERO_ACCESS_EMAIL}</strong> and grant order & inventory permissions.</li>
        <li>Paste your store/marketplace URL below.</li>
      </ol>
    );
  }
  if (platform === "etsy") {
    return (
      <ol className="list-decimal list-inside space-y-1.5">
        <li>Etsy doesn't offer true staff accounts.</li>
        <li>Paste your shop URL below and add a note — we'll follow up on the best way to connect.</li>
      </ol>
    );
  }
  return (
    <p>Tell us which platform you use and how we can get access — paste your store URL and any details below, and we'll follow up to finish connecting.</p>
  );
}

function TextField({ label, testId, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testId} {...props}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue text-sm text-slate-900" />
    </div>
  );
}
